import * as React from "react";
import { Navigate} from "react-router-dom";
import { useAuth } from "./AuthUser";

import "./assets/css/Home.css"
import {Container, Row, Col, Image, Accordion} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {Helmet} from "react-helmet";
import {Link} from "react-router-dom";
import {GoogleBadge, TestimonialCard, testimonials} from "./components/Testimonials";
import chrisDriverSetup from "./assets/img/chris-driver-setup.jpg";
import chrisRangeDrill from "./assets/img/chris-range-drill.jpg";
import swingAnalysisCollage from "./assets/img/swing-analysis-collage.jpg";

// Copy source: Devin's "Revised Home Page" doc (July 2026). Confirmed
// decisions: 30-day guarantee site-wide (not 90), 120,000+ swing reviews,
// Google rating 5.0 (84 reviews).
const problemLessons = [
	{
		title: "You forget half the lesson the moment you leave the range",
		text: "You hear all the right tips next to your instructor — but once you're alone, the exact feel, drill, or position is gone. After a week, the old habits are back.",
	},
	{
		title: "You practice for weeks without knowing if you're doing it right",
		text: "You hit buckets hoping you're repeating the right moves, but deep down you're guessing. Small mistakes compound into bigger problems between lessons.",
	},
	{
		title: "Improvement feels slow, inconsistent, and random",
		text: "One great shot makes you think you've cracked the code — then it disappears the next day. The feedback loop is too slow and too far apart.",
	},
];

const problemYoutube = [
	{
		title: "You don't know which tips apply to your swing",
		text: "One video says “rotate more”, another says “shift your weight”. Without a coach who knows your actual swing, any tip might help — or wreck your mechanics.",
	},
	{
		title: "You try a new tip every week and nothing sticks",
		text: "YouTube is a buffet of swing thoughts. Instead of improving, you layer conflicting ideas on top of each other.",
	},
	{
		title: "You get overwhelmed and don't know what to practice",
		text: "Thousands of videos promise to fix your slice or add 30 yards. Without someone telling you what you personally need, it's just confusion and paralysis.",
	},
];

const steps = [
	{
		icon: "bi-1-circle-fill",
		title: "Record your swing (anytime, anywhere)",
		text: "Use your phone at the range, in your backyard, or indoors. Record one or two angles and send it through your GolfDNA dashboard.",
	},
	{
		icon: "bi-2-circle-fill",
		title: "Get fast, personalized feedback",
		text: "A certified coach reviews your swing and sends back detailed analysis, custom drills, and the exact feels you need to move forward.",
	},
	{
		icon: "bi-3-circle-fill",
		title: "Play with total confidence",
		text: "No more guessing. Start hitting fairways and greens. Start hitting monster drives.",
	},
];

const benefits = [
	{
		icon: "bi-graph-up-arrow",
		title: "You get better, faster",
		text: "With frequent feedback and clear guidance, your swing stops feeling random and starts feeling reliable.",
		sub: "Powered by unlimited swing reviews that keep you on track every time you practice.",
	},
	{
		icon: "bi-bullseye",
		title: "You practice with total confidence",
		text: "No more guessing what's wrong or hoping a YouTube tip will work. You know exactly what to focus on and which drills to use.",
		sub: "Made possible by personalized drills tailored to your swing and your goals.",
	},
	{
		icon: "bi-arrow-repeat",
		title: "Your game transforms and stays consistent",
		text: "You finally eliminate the frustrating ups and downs. Your improvements become permanent, dependable, and repeatable.",
		sub: "Thanks to a coaching system built around modern motor-learning principles.",
	},
];

const features = [
	{
		title: "Unlimited swing reviews",
		text: "Send in your swing as often as you practice and get expert feedback every time — no restrictions, no limits.",
	},
	{
		title: "Personalized drills for your swing",
		text: "Custom drills designed for your mechanics, body motion, and goals — no generic tips.",
	},
	{
		title: "Side-by-side video breakdown",
		text: "Your coach visually compares your swing to proper models and annotates key positions so you can see exactly what to fix.",
	},
	{
		title: "Progress tracking library",
		text: "Every review is saved in your account — a clear timeline of your long-term development.",
	},
	{
		title: "Flexible, on-demand coaching",
		text: "Record anywhere — at the range, indoors, or in your backyard — and get professional guidance on your schedule.",
	},
	{
		title: "Direct access to your coach",
		text: "Ask questions, get clarification, and keep an ongoing dialogue so you're never unsure what to work on.",
	},
];

const faqs = [
	{
		q: "Do I need special equipment?",
		a: "No. Just a smartphone and a place to hit balls.",
	},
	{
		q: "How fast do I get feedback?",
		a: "Typically within 24 hours.",
	},
	{
		q: "Do I need previous lessons?",
		a: "No. GolfDNA works for beginners, children, mid-handicappers, and competitive players.",
	},
	{
		q: "Can I film indoors?",
		a: "Yes — we support indoor simulators, nets, or garage practice.",
	},
	{
		q: "What if I'm busy?",
		a: "Record on your schedule. Even one swing submission a week produces results.",
	},
];

function Home() {
	let auth = useAuth();

	if (auth.user) {
		return <Navigate to="/videos" replace />;
	}

	return (
		<>
			<Helmet>
				<title>Transform Your Game Today | MyGolfDNA.com</title>
				<meta name="description" content="
A membership-based online golf instruction platform for all skill levels, focusing on body protection and maximizing swing efficiency."/>
			</Helmet>
			<style>{`
				#home-hero {
					/* Oversized background (115%) anchored to the left edge pushes
					   Chris (centered in the photo) into the gap between the
					   headline and the signup columns. */
					background:
						linear-gradient(90deg, rgba(16, 23, 31, 0.95) 0%, rgba(16, 23, 31, 0.85) 45%, rgba(16, 23, 31, 0.6) 100%),
						url(${chrisDriverSetup}) 0% 0% / 115% auto no-repeat;
				}
				@media (max-width: 991px) {
					#home-hero {
						background:
							linear-gradient(rgba(16, 23, 31, 0.9), rgba(16, 23, 31, 0.9)),
							url(${chrisDriverSetup}) center 30% / cover no-repeat;
					}
				}
				@media (min-width: 768px) {
					#home-page .home-signup-col {
						margin-top: 48px;
					}
				}
				@media (max-width: 767.98px) {
					/* Consistent 24px edge on phones: sections with g-4/g-5 rows
					   land at 24px with a 24px container; the hero columns keep
					   the default 12px col gutter so their content lines up too. */
					#home-page .container {
						padding-left: 24px;
						padding-right: 24px;
					}
					#home-page .home-hero-copy {
						padding-left: 12px !important;
						padding-right: 12px !important;
					}
				}
				#home-page .home-overline {
					color: var(--orange, #ff5b25);
					font-weight: 700;
					letter-spacing: 2px;
					font-size: 0.85rem;
					text-transform: uppercase;
				}
				#home-page h2 {
					font-size: clamp(1.3rem, 2.6vw, 1.9rem);
					letter-spacing: 1px;
				}
				#home-page .home-stat-number {
					font-size: clamp(1.8rem, 3.6vw, 2.8rem);
					letter-spacing: 1px;
				}
				#home-page .home-stat-label {
					letter-spacing: 2px;
					font-size: 0.78rem;
					text-transform: uppercase;
				}
				#home-page .home-photo {
					border-radius: 16px;
					box-shadow: 0 24px 48px -24px rgba(16, 23, 31, 0.5);
				}
				#home-page .home-check li {
					padding: 6px 0;
					display: flex;
					align-items: flex-start;
					gap: 8px;
				}
				#home-page .home-guarantee {
					border-left: 4px solid var(--orange, #ff5b25);
					background: #fff;
					border-radius: 0 12px 12px 0;
					box-shadow: 0 1px 2px rgba(16, 23, 31, 0.06);
				}
				#home-page .home-problem-card {
					background: #fff;
					border: 1px solid #e7e9ee;
					border-radius: 12px;
					box-shadow: 0 1px 2px rgba(16, 23, 31, 0.04);
				}
				#home-page .home-benefit-icon {
					font-size: 2.2rem;
					color: var(--orange, #ff5b25);
				}
				#home-page .home-cta .btn {
					padding: 0.85rem 3rem;
					font-size: 1.1rem;
					font-weight: 700;
					letter-spacing: 0.5px;
					text-transform: uppercase;
				}
			`}</style>
			<div id="home-page">
				{/* Hero */}
				<div className="position-relative overflow-hidden" id="home-hero">
					<Container>
						{/* align-items-md-center drops the signup block toward the
						    vertical middle of the (taller) headline column. */}
						<Row className="align-items-md-center">
							<div className="col-md-7 p-4 p-lg-5 my-0 my-md-5 home-hero-copy">
								<div className="home-overline mb-3">Ready to see real progress with your golf swing?</div>
								<h1 className="home-heading lh-2 mb-4 text-white text-uppercase">
									Unlimited Swing Reviews.<br/>Super Fast Improvement.<br/>
									<span className="text-secondary">Lower Scores.</span>
								</h1>
								<p className="lead fw-normal text-white" style={{fontSize: 24}}>
									Remote swing reviews are the key to super fast, measurable improvement.
								</p>
								<ul className="list-unstyled text-white mb-4">
									<li><i className="bi bi-check-circle text-success"/> <span className="text-white"><u><b>Unlimited</b></u></span> Coaching for the Price of <span className="text-white"><u><b>One</b></u></span> Lesson </li>
									<li><i className="bi bi-check-circle text-success"/> 1 on 1 Personalized Feedback</li>
									<li><i className="bi bi-check-circle text-success"/> Access to 300+ Golf Instruction Videos</li>
									<li><i className="bi bi-check-circle text-success"/> Practice Plans Built and Perfectly Matched to Your DNA</li>
								</ul>
								<div className="mb-2 d-flex flex-wrap gap-4">
									<GoogleBadge dark />
								</div>
							</div>

							<div className="col p-0 p-lg-4 mb-4 home-signup-col home-hero-copy">
								<h2 className="text-white mt-0">Sign up for <u>FREE</u> Now!</h2>
								<p className="text-white" style={{fontWeight: 300, fontSize: 20}}>Create your free account and get a FREE swing review from a PGA instructor.</p>
								<div className="d-grid gap-1 mt-4">
									<Button className="text-white font-weight-bold" variant="secondary" size="lg" as={Link} to="/free">Sign up for FREE</Button>
									<Button variant="link" as={Link} to="/signin" size="sm" className="text-light text-opacity-75">Already have an account? Sign in</Button>
								</div>
							</div>
						</Row>
					</Container>
				</div>

				{/* Problem — why lessons once a month and YouTube don't work */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Why most golfers <span className="text-secondary">stay stuck</span>
								</h2>
							</Col>
						</Row>
						<Row className="g-4">
							<Col lg={6}>
								<div className="home-problem-card p-4 h-100">
									<h3 className="h5 fw-bold">Traditional golf lessons keep most golfers stuck — not improving.</h3>
									<p className="text-black-50">
										You take a lesson, feel good for a day, then spend the next 4 weeks
										practicing the wrong things, guessing what to fix, and wondering why
										nothing changes.
									</p>
									{problemLessons.map((p) => (
										<div className="mt-3" key={p.title}>
											<div className="fw-semibold">{p.title}</div>
											<div className="text-black-50 small">{p.text}</div>
										</div>
									))}
								</div>
							</Col>
							<Col lg={6}>
								<div className="home-problem-card p-4 h-100">
									<h3 className="h5 fw-bold">You can&apos;t fix your golf swing by watching random YouTube videos.</h3>
									<p className="text-black-50">
										Endless tips, conflicting advice, and swing thoughts that don&apos;t apply
										to <i>your</i> body or <i>your</i> mechanics only make your game more
										confusing — and more frustrating.
									</p>
									{problemYoutube.map((p) => (
										<div className="mt-3" key={p.title}>
											<div className="fw-semibold">{p.title}</div>
											<div className="text-black-50 small">{p.text}</div>
										</div>
									))}
								</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Solution — Chris's story, first person */}
				<div style={{backgroundColor: "#fafbfd", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Here&apos;s the better way to <span className="text-secondary">finally improve</span>
								</h2>
								<p className="text-black-50 mt-2 mb-0" style={{fontWeight: 300, fontSize: 18}}>
									That&apos;s exactly why GolfDNA was created — and why Chris Tyler leads the coaching.
								</p>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6}>
								<Image src={chrisRangeDrill} alt="Chris Tyler working on the range" className="home-photo" fluid />
							</Col>
							<Col lg={6}>
								<p>
									&ldquo;I&apos;ve spent more than a decade coaching golfers of every level,
									from beginners to PGA pros. I&apos;m a certified PGA Instructor, a Director
									of Instruction, and a specialist in video-based coaching and modern swing
									mechanics. I&apos;ve reviewed more than 120,000 swings and given over
									15,000 hours of instruction.
								</p>
								<p>
									I&apos;m not guessing. I&apos;ve spent years studying swings, giving
									thousands of lessons, and building a repeatable, proven process that
									works.&rdquo;
								</p>
								<p className="fw-semibold mb-4">
									— Chris Tyler, Founder of MyGolfDNA
								</p>
								{/* Per Chris (2026-07): don't deep-link the $997 exclusive page
								    from the homepage — send visitors to the regular pricing
								    page, where the exclusive offer sits under the other tiers.
								    Label stays plan-neutral: lower tiers aren't necessarily
								    coached by Chris himself. */}
								<Button
									as={Link}
									to="/pricing"
									variant="secondary"
									size="lg"
									className="fw-semibold text-white"
								>
									See Membership Options
								</Button>
							</Col>
						</Row>
					</Container>
				</div>

				{/* 3-Step System — new step copy + explainer video */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center">
							<Col lg={10} className="pb-4">
								<h2 className="text-center monument-font-black text-primary mb-3 text-uppercase">Our <span className="text-secondary">3-Step System</span> Has <u>Shocked</u> Golfers Worldwide</h2>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col md={12} sm={12} lg={7}>
								<div className="" id="how-it-works">
									{steps.map((step) => (
										<div className="d-flex flex-row mb-4" key={step.title}>
											<div className="d-inline-block px-2">
												<i className={`bi ${step.icon} text-secondary h1`}/>
											</div>
											<div className="d-inline-block align-top px-2">
												<h3 className="monument-font text-uppercase" style={{fontSize: "1.15rem"}}>{step.title}</h3>
												<p className="mb-0">{step.text}</p>
											</div>
										</div>
									))}
								</div>
							</Col>
							<Col lg={5} md={12} sm={12}>
								<div className="home-photo" style={{padding:'56.25% 0 0 0',position:'relative', overflow: 'hidden'}}>
									<iframe
			src="https://player.vimeo.com/video/962579932?h=521d44fbfb&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
			frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
			style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}} title="How it works"/>
								</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Stats band — same proof points as /work-with-chris */}
				<div style={{backgroundColor: "#1B2631", padding: "64px 0"}}>
					<Container>
						<Row className="text-center gy-4">
							<Col md={4}>
								<div className="monument-font-black text-secondary home-stat-number">120,000+</div>
								<div className="text-white-50 home-stat-label mt-2">Swing reviews completed</div>
							</Col>
							<Col md={4}>
								<div className="monument-font-black text-secondary home-stat-number">15,000+</div>
								<div className="text-white-50 home-stat-label mt-2">Hours of golf instruction</div>
							</Col>
							<Col md={4}>
								<div className="monument-font-black text-secondary home-stat-number">PGA &amp; LPGA</div>
								<div className="text-white-50 home-stat-label mt-2">Tour pros coached</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Benefits — 1x3 */}
				<div style={{backgroundColor: "#fafbfd", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Improve faster, build real confidence, and finally play the golf{" "}
									<span className="text-secondary">you know you&apos;re capable of</span>
								</h2>
							</Col>
						</Row>
						<Row className="g-4">
							{benefits.map((b) => (
								<Col lg={4} key={b.title}>
									<div className="text-center px-3">
										<i className={`bi ${b.icon} home-benefit-icon`} />
										<h3 className="h5 fw-bold mt-3 text-capitalize">{b.title}</h3>
										<p className="mb-2">{b.text}</p>
										<p className="text-black-50 small mb-0">{b.sub}</p>
									</div>
								</Col>
							))}
						</Row>
					</Container>
				</div>

				{/* Features + real lesson screens */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									This is what your lessons <span className="text-secondary">look like</span>
								</h2>
								<p className="text-black-50 mt-2 mb-0" style={{fontWeight: 300, fontSize: 18}}>
									Real member swing reviews: side-by-side video, frame-by-frame analysis, clear next steps.
								</p>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6}>
								<Image src={swingAnalysisCollage} alt="Examples of swing analysis reviews" className="home-photo" fluid />
							</Col>
							<Col lg={6}>
								<ul className="list-unstyled home-check mb-0">
									{features.map((f) => (
										<li key={f.title}>
											<i className="bi bi-check-circle text-success" />
											<span><b>{f.title}.</b> {f.text}</span>
										</li>
									))}
								</ul>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Testimonials — review cards on the dark brand background,
				    same treatment as /work-with-chris */}
				<div id="testimonials" style={{backgroundColor: "#1B2631", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-2">
							<Col lg={8}>
								<h2 className="monument-font-black text-white text-uppercase">
									Our students make <span className="text-secondary">insane progress</span>
								</h2>
								<p className="text-white-50 mt-2" style={{fontWeight: 300}}>Don&apos;t take our word for it:</p>
							</Col>
						</Row>
						<Row className="justify-content-center text-center mb-4">
							<Col className="d-flex flex-wrap justify-content-center gap-4">
								<GoogleBadge dark />
							</Col>
						</Row>
						<Row className="g-4">
							{/* Phones get the first 3 reviews only — 6 stacked cards
							    made the section feel endless on mobile. */}
							{testimonials.map((t, i) => (
								<Col lg={4} md={6} key={t.name} className={i >= 3 ? "d-none d-md-block" : ""}>
									<TestimonialCard {...t} />
								</Col>
							))}
						</Row>
					</Container>
				</div>

				{/* FAQ */}
				<div style={{backgroundColor: "#fafbfd", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center">
							<Col lg={8}>
								<h2 className="text-center monument-font-black text-primary text-uppercase mb-4">
									Frequently asked <span className="text-secondary">questions</span>
								</h2>
								<Accordion flush>
									{faqs.map((f, i) => (
										<Accordion.Item eventKey={String(i)} key={f.q}>
											<Accordion.Header>{f.q}</Accordion.Header>
											<Accordion.Body>{f.a}</Accordion.Body>
										</Accordion.Item>
									))}
								</Accordion>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Guarantee + Final CTA — white before the dark footer */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center mb-5">
							<Col lg={10}>
								<div className="home-guarantee p-4">
									<div className="fw-semibold mb-1">Our Improvement Guarantee</div>
									<div className="fw-bold" style={{fontSize: 18}}>
										If you use GolfDNA consistently for 30 days and don&apos;t feel more
										confident, more consistent, and more in control of your golf swing,
										we&apos;ll refund your first month and cancel your subscription — no
										questions asked.
									</div>
									<div className="text-black-50 mt-1">
										We&apos;re confident because the model works. It&apos;s not luck, it&apos;s science.
									</div>
								</div>
							</Col>
						</Row>
						<Row className="justify-content-center text-center">
							<Col lg={8}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Ready to improve <span className="text-secondary">faster than ever?</span>
								</h2>
								<p className="text-black-50 mt-3" style={{fontWeight: 300, fontSize: 18}}>
									Join thousands of golfers who finally understand their swing — and have
									the coaching to fix it.
								</p>
								<div className="home-cta mt-4">
									<Button as={Link} to="/pricing" variant="secondary" size="lg" className="text-white">Start Improving Today</Button>
								</div>
								<div className="text-black-50 small mt-3">
									Unlimited coaching. Real progress. No more guesswork. · Or <Link to="/free">start with a FREE swing review</Link>
								</div>
							</Col>
						</Row>
					</Container>
				</div>
			</div>

		</>
	);
}

export default Home;
